import type { ComponentDocs, ExampleHost } from '../component-docs.js';

/**
 * What `<caos-topbar>` says about itself.
 *
 * IT REPORTS, IT DOES NOT ACT. Every control in the bar raises an event and stops there: the theme
 * button does not change the theme, choosing Setup does not open Setup, "Sign out" does not sign
 * anybody out. The bar displays the values it is handed and tells its host what was pressed. The
 * theme button is the one where that costs the reader something, because the bar has no way to know
 * what the page's theme actually is: leave `theme` unset on a dark page and the control reads
 * "Theme: System" while saying nothing true, and pressing it does nothing. So the first example
 * declares a host that does the host's half — it stamps the page's current theme on the bar, and on
 * press it cycles the theme for real, so the page changes and the control's label follows it.
 *
 * TWO LISTS ARE PROPERTIES, AND EACH ONE DECIDES WHETHER ITS BLOCK EXISTS. `personaOptions` and
 * `impersonationOptions` are arrays, so they cannot ride attributes; and an empty one hides its
 * whole block in the account menu rather than showing an empty submenu. That is how a person who
 * may not impersonate simply never sees "Log in as another user".
 *
 * THE WORDMARK IS A BRAND TOKEN READ IN SCRIPT. `--brand-wordmark` and `--brand-os-suffix` are not
 * read by the stylesheet — they are resolved in JavaScript from the computed style and written into
 * the lockup, with the `wordmark` and `os-suffix` attributes overriding them. They are listed in the
 * token table anyway, because somebody white-labelling the platform needs to know that setting those
 * two tokens repaints this bar, and a mutation observer on the root's `data-brand` makes it repaint
 * live with no reload.
 */

/**
 * The host's half of the theme control, shared by every example here.
 *
 * The bar DISPLAYS a setting and reports a press; owning the setting is the surrounding page's job.
 * That page's setting is handed in rather than reimplemented here, and the difference matters: a
 * private copy read the attribute stamped on the document instead of the stored preference, so
 * pressing the example turned the page dark while the real control above it still said System,
 * pressing the real one left the example claiming Dark on a light page, and neither choice survived
 * a reload. Two controls for one setting that disagree is a worse lesson than no example.
 *
 * It is on EVERY example rather than only the first, because the alternative is an example that
 * writes a `theme` value into its own markup and thereby displays a setting that may not be the
 * page's — the same defect, reintroduced one example later.
 */
const THEME_HOST: ExampleHost = {
  summary:
    'The surrounding page owns the theme: the bar shows the page’s current setting, and a press cycles it ' +
    'for real — the same setting the control at the top of this page and the Appearance preference drive, ' +
    'so all three agree the instant any of them is used.',
  run: (stage, page) => {
    const bar = stage.querySelector('caos-topbar');
    if (!bar) return;
    bar.setAttribute('theme', page.theme.current());
    bar.addEventListener('topbar-theme', () => bar.setAttribute('theme', page.theme.cycle()));
    // Changed from anywhere else — the real bar above, the Appearance preference — and this follows.
    const stop = page.theme.onChange((pref) => {
      if (bar.isConnected) bar.setAttribute('theme', pref);
      else stop();
    });
  },
};

const docs: ComponentDocs = {
  aliases: ['header', 'masthead', 'global header', 'app bar', 'navbar', 'account menu', 'user menu'],

  answers: [
    'show who is signed in and let them sign out',
    'give somebody one place to reach Setup and their profile',
    'let somebody switch between light and dark',
    'warn people they are not on production',
    'let an administrator look at the app as somebody else',
  ],

  // The frame's own top band. It appears once, above everything, and nowhere else — a second one
  // inside a page would be a second claim about who is signed in.
  placements: ['seam'],

  inputs: [
    {
      name: 'userName',
      attribute: 'user-name',
      type: 'text',
      description:
        'The signed-in person’s display name. It sets the avatar’s two initials, the name in the bar and the ' +
        'name at the head of the account menu. With no name at all the whole account control is hidden, which ' +
        'is what a bar above a sign-in screen looks like.',
      // A fact about the person reading the page, not text an author types once when placing the
      // bar (CAOS-562).
      source: 'userContext',
    },
    {
      name: 'userSub',
      attribute: 'user-sub',
      type: 'text',
      description: 'The line under the name — a role, a company, an email. Drawn small, uppercase and clipped at a width.',
      // CAOS-1811 — a fact about the person reading, exactly as `userName` above is, and it shipped
      // declaring nothing. An absent source means `literal`, so a placed bar drew whatever the page
      // author typed when they placed it — to every reader. "Admin · Contoso" is true for the one
      // administrator who placed it and a false claim about everybody else who opens that screen,
      // and unlike a missing value it does not look wrong to the person who set it.
      //
      // The shell has never had this problem: `shell-frame.ts` sets the attribute from
      // `runtime.user.sub`, derived per session. So the hand-built path was right and the declared
      // contract was not, which is the split CAOS-798, CAOS-159, CAOS-1806 and CAOS-1809 each found
      // one input at a time. `viewerInputContext` already carries `user_sub` for this, so nothing in
      // the renderer changes — only this declaration and the manifests generated from it.
      source: 'userContext',
    },
    {
      name: 'theme',
      attribute: 'theme',
      type: 'text',
      options: ['system', 'light', 'dark'],
      description:
        'Which theme the page is on: system, light or dark. The bar only DISPLAYS this — the button shows where ' +
        'a press would take you (a moon while light or system, a sun while dark) and the label names where you ' +
        'are. The host has to keep it true.',
      default: 'system',
    },
    {
      name: 'env',
      attribute: 'env',
      type: 'text',
      description:
        'The environment chip beside the wordmark — “SANDBOX”, “TEST”. Drawn in the warning colour with a soft ' +
        'hairline and no fill. Unset ⇒ no chip, which is what production looks like.',
    },
    {
      name: 'wordmark',
      attribute: 'wordmark',
      type: 'text',
      description:
        'Override the brand name in the lockup. Unset ⇒ the `--brand-wordmark` token from the compiled token ' +
        'floor. Cleared to empty, no wordmark is drawn — an org may deliberately have no company name here.',
    },
    {
      name: 'osSuffix',
      attribute: 'os-suffix',
      type: 'text',
      description:
        'The accent line under the wordmark. Unset ⇒ the `--brand-os-suffix` token from the compiled ' +
        'token floor — the platform identity that stays put when a tenant sets their own name above it. ' +
        'Cleared to empty, no tagline is drawn.',
    },
    {
      name: 'showSetup',
      attribute: 'show-setup',
      type: 'boolean',
      description:
        'Offer Setup in the gear menu. A permission statement: leave it off for somebody who may not configure ' +
        'the platform. The gear itself is drawn whenever it has anything to hold — with this off and ' +
        '`show-edit-page` on it still appears, carrying that entry alone, so a control the host claimed is ' +
        'never left unreachable.',
      default: false,
    },
    {
      name: 'showEditPage',
      attribute: 'show-edit-page',
      type: 'boolean',
      description:
        'Offer “Edit this page” in the gear menu, under a divider below Setup. The bar cannot edit anything, so ' +
        'this is the host declaring it has an editor to open; left off, the entry is not rendered at all.',
      default: false,
    },
    {
      name: 'personaOptions',
      // No `attribute`: a list of personas cannot ride an attribute. It is set as a property.
      type: 'json',
      default: [],
      description:
        'The permission-set groups this person may preview as, as `{ key, label, description? }`. An empty list ' +
        'hides the whole “Viewing as:” block. The menu says plainly that the switch previews what is VISIBLE and ' +
        'does not change which rows the data layer returns.',
    },
    {
      name: 'activePersonaKey',
      attribute: 'active-persona-key',
      type: 'text',
      description: 'Which persona is being previewed. Its row in the submenu carries the tick.',
    },
    {
      name: 'personaLabel',
      attribute: 'persona-label',
      type: 'text',
      description:
        'What the “Viewing as:” line reads. Given explicitly it wins; otherwise it is derived from the active key ' +
        'against the offered personas, and only a key matching nothing falls back to “Default”.',
      default: 'Default',
    },
    {
      name: 'impersonationOptions',
      type: 'json',
      default: [],
      description:
        'The people an administrator may look at the app as, as `{ id, label }`. An empty list hides the “Log in ' +
        'as another user” block entirely, so it is only ever offered to somebody who may use it.',
    },
  ],

  events: [
    { name: 'topbar-theme', when: 'The theme button was pressed. The bar does not change the theme — it says it was asked to.', carries: 'nothing' },
    { name: 'topbar-setup', when: '“Setup” was chosen from the gear menu — the gear itself only opens the menu.', carries: 'nothing' },
    { name: 'topbar-profile', when: '“View my profile” was chosen from the account menu.', carries: 'nothing' },
    { name: 'topbar-edit-page', when: '“Edit this page” was chosen from the gear menu — the host binds whatever edits the surface underneath.', carries: 'nothing' },
    { name: 'topbar-my-feedback', when: '“My feedback” was chosen: what this person has reported, and what happened to it.', carries: 'nothing' },
    {
      name: 'topbar-persona',
      when: 'A persona was picked from “Viewing as:”, or “Reset to default” was chosen there.',
      carries: '`{ key, label }` — both null on a reset.',
    },
    {
      name: 'topbar-impersonate',
      when: 'Somebody was picked from “Log in as another user”.',
      carries: '`{ id, label }` — who to look at the app as.',
    },
    { name: 'topbar-signout', when: '“Sign out” was chosen. Ending the session is the host’s half.', carries: 'nothing' },
  ],

  state: [],

  slots: [
    { name: 'center', takes: 'The middle column, which is otherwise empty — the global search box goes here.' },
    {
      name: 'commands',
      takes:
        'Extra command buttons at the head of the right-hand cluster, before the Setup gear. A slotted ' +
        '`<button slot="commands">` is given the same bordered, accent-glyph treatment as the bar’s own.',
    },
  ],

  tokens: [
    { token: '--caos-color-bg', controls: 'The bar’s own frosted band — the PAGE base, not a surface, so the chrome reads continuous with the page.' },
    { token: '--caos-color-border', controls: 'The hairline under the bar, the command buttons’ outlines, the rule beside the account, and the menu’s outline.' },
    { token: '--caos-color-border-strong', controls: 'A command button’s outline while the pointer is over it.' },
    { token: '--caos-color-surface', controls: 'The account menu’s background.' },
    { token: '--caos-color-surface-2', controls: 'A menu row’s fill on hover.' },
    { token: '--caos-color-accent', controls: 'Every command glyph, the avatar’s gradient, and the tick beside the persona in force.' },
    { token: '--caos-color-accent-strong', controls: 'The far end of the avatar’s gradient.' },
    { token: '--caos-color-text', controls: 'The wordmark, the signed-in name, and the menu rows.' },
    { token: '--caos-color-text-muted', controls: 'The menu’s sub-line, the row glyphs, and the submenu notes.' },
    { token: '--caos-color-text-ghost', controls: 'The small uppercase line under the name, and the submenu carets.' },
    { token: '--caos-color-warning', controls: 'The environment chip’s hairline, mixed down to about a third.' },
    { token: '--caos-color-warning-text', controls: 'The environment chip’s text.' },
    { token: '--caos-focus-ring', controls: 'The ring on a command button reached by keyboard.' },
    { token: '--caos-font-display', controls: 'The wordmark and the avatar’s initials.' },
    { token: '--caos-font-mono', controls: 'The accent suffix line, the environment chip, and the small line under the name.' },
    { token: '--caos-radius-sm', controls: 'The corner radius of the command buttons, the environment chip and the avatar.' },
    { token: '--caos-shadow-float', controls: 'The shadow that lifts the account menu off the bar.' },
    {
      token: '--brand-wordmark',
      controls: 'The brand name in the lockup. Read in script rather than by the stylesheet, and overridden by the `wordmark` attribute.',
    },
    {
      token: '--brand-os-suffix',
      controls: 'The accent line under the wordmark. Read in script, and overridden by the `os-suffix` attribute.',
    },
  ],

  parts: [
    { part: 'brand', is: 'the brand lockup' },
    { part: 'brand-primary', is: 'the first word of the wordmark' },
    { part: 'brand-secondary', is: 'the accent suffix under it' },
    { part: 'env', is: 'the environment chip' },
    { part: 'setup-block', is: 'the Setup gear and its menu together' },
    { part: 'setup', is: 'the Setup gear — the button that opens the menu' },
    { part: 'theme', is: 'the theme button' },
    { part: 'account', is: 'the account control at the right' },
  ],

  methods: [],

  placing:
    'It is the first band of the workspace frame, above the app’s tab row, and it sticks to the top of the ' +
    'viewport as the page scrolls under it. Nothing places it but the shell, and it needs a host for every ' +
    'control it carries: something that changes the theme, opens Setup, navigates to the profile, and ends the ' +
    'session. Below about 1280px the name and its sub-line drop and the avatar stays; below 720px the lockup ' +
    'folds onto one line and the account control collapses to the avatar — it is never hidden, because hiding ' +
    'it would take sign-out away entirely on a phone.',

  whenToUse:
    'Only as the platform’s top bar. It is the one place identity, the environment, and the session-level ' +
    'controls live, and there is exactly one of it per screen.',

  insteadUse: [
    { name: 'WorkspaceTabs', when: 'You want the app-scoped row of destinations that sits under this bar.' },
    { name: 'PageHeader', when: 'You want to name the surface a person is looking at, not the platform they are signed in to.' },
    { name: 'ActionBar', when: 'The controls act on the record or the list in front of them rather than on the session.' },
    { name: 'ImpersonationBanner', when: 'You need to SHOW that a session is being viewed as somebody else; this bar only starts that.' },
  ],

  accessibility:
    'The bar builds real buttons throughout: the account trigger carries `aria-haspopup="menu"` and an ' +
    '`aria-expanded` kept in step, the dropdown is a `role="menu"` labelled “Account menu” with `role="menuitem"` ' +
    'rows, the persona rows are `role="menuitemradio"` with `aria-checked` tracking the active key, and the two ' +
    'submenus keep their own `aria-expanded`. The theme button’s accessible name states the CURRENT preference ' +
    'rather than the glyph, so a screen reader hears “Theme: Dark” rather than “sun”. Escape and a click outside ' +
    'close the menu, and every glyph is hidden from assistive technology. What is left to the author: focus is ' +
    'not moved into the menu when it opens, is not trapped there, and does not return to the trigger when it ' +
    'closes; and anything projected into the `commands` slot brings its own accessible name — those buttons are ' +
    'usually glyph-only.',

  examples: [
    {
      key: 'signed-in',
      label: 'Signed in',
      summary:
        'The whole bar as a person sees it at work: the lockup, the Setup gear, the theme control, and an account ' +
        'menu holding the profile, the persona preview, Log in as, and sign out.',
      placement: {
        component: 'caos-topbar',
        inputs: {
          userName: 'Dana Whitfield',
          userSub: 'Sales manager · Meridian Supply',
          wordmark: 'Cloud Atlantis',
          osSuffix: 'OS',
          showSetup: true,
          personaLabel: 'Sales manager',
          activePersonaKey: 'sales-manager',
          personaOptions: [
            { key: 'sales-manager', label: 'Sales manager', description: 'Works accounts and orders' },
            { key: 'admin', label: 'Administrator', description: 'Configures the platform' },
          ],
          impersonationOptions: [{ id: 'u2', label: 'Marcus Ellery' }],
        },
      },
      // The account button opens a menu (profile, theme, viewing-as, log in as, sign out) laid out INSIDE
      // the bar, so it needs the room reserved on the stage — it has no top layer of its own to float
      // into. On a real screen the bar is at the top of the page and nothing is below it.
      stage: { opensBelow: 296 }, // measured: 286px menu + 8px gap
      host: THEME_HOST,
    },
    {
      key: 'no-user',
      label: 'Nobody signed in, on a test environment',
      summary:
        'The same bar with no signed-in name: the account control is hidden entirely rather than drawn empty, ' +
        'and the environment chip says where you are.',
      placement: {
        component: 'caos-topbar',
        inputs: {
          wordmark: 'Cloud Atlantis',
          osSuffix: 'OS',
          env: 'Sandbox',
        },
      },
      host: THEME_HOST,
    },
  ],
};

export default docs;
